import loggerGenerator from '@travel/logger';
import { noticeError } from '@travel/tracker';

import { setCommonError } from 'store/commonError/slice';
import { getMatchingPath } from 'store/commonError/utils';

import { apiPathMap } from 'constants/api';
import { ApiErrorCodes, GENERAL_ERROR_LABEL } from 'store/apiTypes';

import type { CommonError, InlineError, PopupError } from 'store/commonError/types';
import type { Store } from 'react-redux';
const logger = loggerGenerator();

const setCommonErrorHandler =
  (store: Store) =>
  async ({
    status,
    url,
    response,
    method = 'default',
  }: {
    status: number;
    url: string;
    response?: Response;
    method?: string;
  }) => {
    const parsedURL = new URL(url);
    const path = parsedURL?.pathname;
    const matchingPath = getMatchingPath(path);

    let error: CommonError = {
      message: 'Common API Error',
      httpStatusCode: status,
      type: 'redirect',
    };

    try {
      const json = await response?.json();

      logger.error('API Error', { url, status, error: json });

      if (json.message) error.message = json.message;

      if (!matchingPath) {
        throw new Error('API Path Not Found');
      } else {
        const pathConfig = apiPathMap.get(matchingPath);
        const methodConfig = pathConfig?.[method.toUpperCase()] || pathConfig?.default;

        const isPopup = methodConfig?.popup?.includes(status) ?? false;
        const isInline = methodConfig?.inline?.includes(status) ?? false;

        if (isPopup) {
          const popupError: PopupError = {
            type: 'popup',
            code: json.code,
            labelId: ApiErrorCodes[json.code] ?? GENERAL_ERROR_LABEL,
          };

          error = {
            ...error,
            ...popupError,
          };
        } else if (isInline) {
          const jsonArray = Array.isArray(json) ? json : [json];

          const inlineError: InlineError = {
            type: 'inline',
            fields: jsonArray.map((field) => ({
              fieldName: field?.fieldName,
              code: field?.code,
              labelId: ApiErrorCodes[field?.code],
            })),
          };

          error = {
            ...error,
            ...inlineError,
          };
        }

        store.dispatch(setCommonError(error));
      }

      if (/^50/.test(String(response?.status))) {
        noticeError('API 50x Error', {
          url: response?.url,
          res: response,
          json,
        });
      } else if (/^40/.test(String(response?.status))) {
        noticeError('API 40x Error', {
          url: response?.url,
          res: response,
          json,
        });
      } else {
        noticeError(`${status} error`, {
          url: response?.url,
          res: response,
          json,
        });
      }
    } catch (error) {
      const commonError: CommonError = {
        ...error,
        type: 'redirect',
      };

      store.dispatch(setCommonError(commonError));
      logger.error(`Internal error`, url, status, error);
      noticeError(`${status} error`, {
        url,
        res: response,
      });
    }
  };

export default setCommonErrorHandler;
